import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { TopBar } from "@/components/PageShell";
import { CourtLabel } from "@/components/CourtLabel";
import {
  Newspaper, ExternalLink, Search, Sparkles, AlertCircle, Loader2,
  Clock, RefreshCw, Instagram, Video, Landmark, Gavel, TrendingUp, Rocket,
  Building2, Scale, X, ArrowRight,
} from "lucide-react";

const NAVY = "#0E1D2D";
const GOLD = "#C9953D";

const CATEGORIES = [
  { key: "all", label: "Destaques", icon: Sparkles },
  { key: "juridico", label: "Direito", icon: Scale },
  { key: "tribunais", label: "Tribunais", icon: Gavel },
  { key: "legislacao", label: "Legislação", icon: Landmark },
  { key: "economia", label: "Economia", icon: TrendingUp },
  { key: "negocios", label: "Negócios", icon: Building2 },
  { key: "inovacao", label: "Inovação", icon: Rocket },
];

const fmtDT = (s) => s
  ? new Date(s).toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" })
  : "";
const fmtHM = (s) => s
  ? new Date(s).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })
  : "";

export default function Noticias() {
  const [state, setState] = useState({ items: [], featured: [], social: [],
    lastUpdated: null, nextUpdate: null, loading: true, error: null });
  const [category, setCategory] = useState("all");
  const [q, setQ] = useState("");
  const [explainOpen, setExplainOpen] = useState(null); // article

  const load = async () => {
    setState((s) => ({ ...s, loading: true, error: null }));
    try {
      const r = await api.get("/news");
      setState({
        items: r.data.articles || [],
        featured: r.data.featured || [],
        social: r.data.social || [],
        lastUpdated: r.data.last_updated,
        nextUpdate: r.data.next_update,
        loading: false, error: null,
      });
    } catch (e) {
      const detail = e?.response?.data?.detail || "Serviço indisponível";
      setState((s) => ({ ...s, loading: false, error: detail }));
    }
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    return state.items.filter((n) => {
      if (category !== "all" && n.category !== category) return false;
      if (!term) return true;
      const hay = `${n.title} ${n.excerpt || ""} ${n.source_name || ""}`.toLowerCase();
      return hay.includes(term);
    });
  }, [state.items, q, category]);

  // Manchete = primeiro featured OR primeiro do array
  const hero = state.featured?.[0] || filtered[0] || null;
  const heroSecondaries = (state.featured?.slice(1, 3) || filtered.slice(1, 3));
  const gridItems = filtered.filter((n) => n.id !== hero?.id && !heroSecondaries.find(x => x.id === n.id));

  const bySection = (sectionCategory) => filtered.filter((n) => n.category === sectionCategory).slice(0, 6);

  return (
    <div className="flex-1 min-w-0 flex flex-col" data-testid="noticias-page">
      <TopBar/>
      <div className="flex-1 p-3 sm:p-6 lg:p-8 font-sans">
        {/* Header editorial */}
        <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
          <div>
            <div className="text-[10px] uppercase tracking-[0.28em] font-bold" style={{ color: GOLD }}>
              <Newspaper className="w-3 h-3 inline mr-1"/> Hermes Notícias
            </div>
            <h1 className="font-heading text-4xl mt-1" style={{ color: NAVY }}>
              O que realmente importa para o Direito.
            </h1>
            {state.lastUpdated && (
              <div className="mt-2 text-[12px] text-slate-500 flex items-center gap-3">
                <span data-testid="noticias-last-updated">
                  <Clock className="w-3 h-3 inline mr-1"/> Atualizado às {fmtHM(state.lastUpdated)}
                </span>
                {state.nextUpdate && (
                  <span className="text-slate-400">
                    Próxima atualização: {fmtHM(state.nextUpdate)}
                  </span>
                )}
                <button onClick={load} className="text-slate-400 hover:text-slate-700 inline-flex items-center gap-1"
                        data-testid="noticias-refresh">
                  <RefreshCw className="w-3 h-3"/> atualizar
                </button>
              </div>
            )}
          </div>
          <div className="relative">
            <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2"/>
            <input value={q} onChange={(e) => setQ(e.target.value)}
              placeholder="Buscar notícias, decisões ou temas…"
              data-testid="noticias-search"
              className="h-10 w-80 max-w-full pl-9 pr-3 rounded-lg border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#c9953d]/40 focus:border-[#c9953d]"/>
          </div>
        </div>

        {/* Banner editorial Hermes — "Informação certa. Decisão mais forte." */}
        <div className="rounded-2xl overflow-hidden border border-slate-800 bg-gradient-to-br from-[#0B1B2F] to-[#071B34] mb-6 relative"
             data-testid="noticias-hero-banner">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-0 items-stretch">
            <div className="md:col-span-2 p-6 md:p-10 flex flex-col justify-center relative">
              <div className="text-[10.5px] uppercase tracking-[0.32em] font-bold text-amber-300 mb-2">Hermes · Editorial</div>
              <h2 className="font-heading text-2xl md:text-4xl font-semibold text-white leading-tight italic">
                &ldquo;Informação certa. Decisão mais forte.&rdquo;
              </h2>
              <p className="text-white/75 text-sm md:text-base mt-3 max-w-xl">
                Panorama diário das fontes oficiais brasileiras — decisões, atos normativos, jurisprudência
                e movimentações que impactam o exercício da advocacia. Curadoria transparente e sem invenção.
              </p>
              <div className="mt-4 flex items-center gap-3 text-[11px] uppercase tracking-widest font-semibold text-amber-200/90">
                <span>Estratégia</span><span className="text-white/30">·</span>
                <span>Informação</span><span className="text-white/30">·</span>
                <span>Resultados</span>
              </div>
            </div>
            <div className="relative min-h-[180px] md:min-h-[260px] bg-gradient-to-l from-black/30 to-transparent">
              <img
                src="https://customer-assets-cm19k8pv.emergentagent.net/job_doc-advocate-1/artifacts/bxdmfc4h_image.png"
                alt="Hermes — Informação certa. Decisão mais forte."
                data-testid="noticias-hero-image"
                className="absolute inset-0 w-full h-full object-cover object-center"
                loading="lazy"
              />
              <div className="absolute inset-y-0 left-0 w-16 bg-gradient-to-r from-[#0B1B2F] to-transparent pointer-events-none"/>
            </div>
          </div>
        </div>

        {/* Categorias */}
        <div className="flex items-center gap-2 flex-wrap mb-6 overflow-x-auto">
          {CATEGORIES.map((c) => {
            const Icon = c.icon;
            const active = category === c.key;
            return (
              <button key={c.key} onClick={() => setCategory(c.key)}
                data-testid={`noticias-cat-${c.key}`}
                className={`h-8 px-3 rounded-full text-[12px] font-semibold border inline-flex items-center gap-1.5 transition whitespace-nowrap ${
                  active ? "bg-slate-900 text-white border-slate-900"
                        : "bg-white text-slate-700 border-slate-200 hover:border-slate-300"
                }`}>
                <Icon className="w-3.5 h-3.5"/> {c.label}
              </button>
            );
          })}
        </div>

        {state.loading ? (
          <NewsSkeletonHero/>
        ) : state.error ? (
          <NewsErrorState message={state.error} onRetry={load}/>
        ) : filtered.length === 0 ? (
          <NewsEmptyState onRetry={load}/>
        ) : (
          <>
            {/* Hero editorial */}
            {hero && (
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-8" data-testid="noticias-hero">
                <ArticleCard n={hero} size="hero" onExplain={() => setExplainOpen(hero)}/>
                <div className="grid grid-cols-1 gap-4">
                  {heroSecondaries.map((n) => (
                    <ArticleCard key={n.id} n={n} size="secondary" onExplain={() => setExplainOpen(n)}/>
                  ))}
                </div>
              </div>
            )}

            {/* Decisões que importam · Tribunais */}
            {bySection("tribunais").length > 0 && (
              <SectionBlock title="Decisões que importam" tag="TRIBUNAIS" items={bySection("tribunais")} onExplain={setExplainOpen}/>
            )}

            {/* Hermes nas redes */}
            {state.social.length > 0 && <SocialBlock posts={state.social}/>}

            {/* Novas leis e normas */}
            {bySection("legislacao").length > 0 && (
              <SectionBlock title="Novas leis e normas" tag="LEGISLAÇÃO" items={bySection("legislacao")} onExplain={setExplainOpen}/>
            )}

            {/* Inovação */}
            {bySection("inovacao").length > 0 && (
              <SectionBlock title="Inovação & Tecnologia" tag="INOVAÇÃO" items={bySection("inovacao")} onExplain={setExplainOpen}/>
            )}

            {/* Economia e negócios */}
            {(bySection("economia").length + bySection("negocios").length) > 0 && (
              <SectionBlock title="Economia & Negócios" tag="MERCADO"
                items={[...bySection("economia"), ...bySection("negocios")].slice(0, 6)}
                onExplain={setExplainOpen}/>
            )}

            {/* Últimas */}
            {gridItems.length > 0 && (
              <SectionBlock title="Últimas notícias" tag="RECENTES" items={gridItems.slice(0, 12)} onExplain={setExplainOpen}/>
            )}
          </>
        )}
      </div>

      {explainOpen && <ExplainDrawer article={explainOpen} onClose={() => setExplainOpen(null)}/>}
    </div>
  );
}

// ---------------------------------------------------------------------------
function ArticleCard({ n, size = "grid", onExplain }) {
  const isHero = size === "hero";
  const isSec = size === "secondary";
  return (
    <article data-testid={`news-card-${n.id}`}
      className={`rounded-2xl border border-slate-200 bg-white overflow-hidden group hover:shadow-lg transition ${
        isHero ? "" : ""
      }`}>
      <div className={`relative overflow-hidden bg-slate-100 ${isHero ? "aspect-[16/9]" : "aspect-[16/9]"}`}>
        <ImageWithFallback src={n.image_url} alt={n.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"/>
        {n.court && (
          <div className="absolute top-3 left-3">
            <CourtLabel raw={n.court} size="xs" variant="chip"/>
          </div>
        )}
      </div>
      <div className={`p-4 ${isHero ? "p-6" : ""}`}>
        <div className="flex items-center gap-2 text-[10.5px] uppercase tracking-widest text-slate-500 mb-1">
          <span className="font-bold" style={{ color: GOLD }}>{n.source_name}</span>
          <span>·</span>
          <span>{fmtDT(n.published_at)}</span>
        </div>
        <h2 className={`font-heading font-semibold ${isHero ? "text-2xl leading-tight" : isSec ? "text-base" : "text-[15px]"}`}
            style={{ color: NAVY }}>
          {n.title}
        </h2>
        {n.excerpt && !isSec && (
          <p className={`text-slate-600 mt-2 ${isHero ? "text-sm" : "text-[13px]"} line-clamp-3`}>
            {n.excerpt}
          </p>
        )}
        <div className="mt-3 flex items-center gap-2 flex-wrap">
          <a href={n.original_url} target="_blank" rel="noopener noreferrer"
            data-testid={`news-source-${n.id}`}
            className="inline-flex items-center gap-1 h-8 px-3 rounded-lg text-white text-[12px] font-semibold hover:opacity-95"
            style={{ background: NAVY }}>
            Ler na fonte <ExternalLink className="w-3 h-3"/>
          </a>
          <button onClick={onExplain} data-testid={`news-explain-${n.id}`}
            className="h-8 px-3 rounded-lg border border-slate-200 bg-white text-slate-700 text-[12px] font-semibold hover:border-slate-300 inline-flex items-center gap-1">
            <Sparkles className="w-3 h-3" style={{ color: GOLD }}/> Por que isso importa?
          </button>
        </div>
      </div>
    </article>
  );
}

function ImageWithFallback({ src, alt, className }) {
  const [phase, setPhase] = useState("direct"); // direct → proxy → error
  if (!src || phase === "error") {
    return (
      <div className={`bg-gradient-to-br from-slate-800 to-slate-950 flex items-center justify-center ${className}`}>
        <Newspaper className="w-10 h-10 text-slate-500"/>
      </div>
    );
  }
  const backend = process.env.REACT_APP_BACKEND_URL || "";
  const proxied = `${backend}/api/news/image-proxy?url=${encodeURIComponent(src)}`;
  const finalSrc = phase === "proxy" ? proxied : src;
  return (
    <img src={finalSrc} alt={alt || ""} loading="lazy"
         referrerPolicy="no-referrer"
         className={className}
         onError={() => setPhase(p => (p === "direct" ? "proxy" : "error"))}/>
  );
}

function SectionBlock({ title, tag, items, onExplain }) {
  if (!items?.length) return null;
  return (
    <section className="mb-10" data-testid={`news-section-${tag.toLowerCase()}`}>
      <div className="flex items-baseline justify-between mb-3">
        <div>
          <div className="text-[10.5px] uppercase tracking-[0.28em] font-bold" style={{ color: GOLD }}>{tag}</div>
          <h2 className="font-heading text-2xl font-semibold" style={{ color: NAVY }}>{title}</h2>
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((n) => <ArticleCard key={n.id} n={n} onExplain={() => onExplain(n)}/>)}
      </div>
    </section>
  );
}

function SocialBlock({ posts }) {
  const insta = posts.filter(p => p.platform === "instagram").slice(0, 3);
  const tiktok = posts.filter(p => p.platform === "tiktok").slice(0, 3);
  return (
    <section className="mb-10" data-testid="news-social-block">
      <div className="text-[10.5px] uppercase tracking-[0.28em] font-bold" style={{ color: GOLD }}>Redes Hermes</div>
      <h2 className="font-heading text-2xl font-semibold mb-3" style={{ color: NAVY }}>Hermes nas redes</h2>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <SocialColumn title="Instagram" icon={Instagram} tone="rose" posts={insta}/>
        <SocialColumn title="TikTok" icon={Video} tone="slate" posts={tiktok}/>
      </div>
    </section>
  );
}

function SocialColumn({ title, icon: Icon, tone, posts }) {
  const border = tone === "rose" ? "border-rose-200" : "border-slate-200";
  if (posts.length === 0) return (
    <div className={`rounded-2xl border ${border} bg-white p-6 text-center`}>
      <Icon className="w-6 h-6 mx-auto text-slate-300 mb-2"/>
      <div className="text-sm text-slate-500">Nenhuma publicação em destaque no {title}.</div>
    </div>
  );
  return (
    <div className={`rounded-2xl border ${border} bg-white p-4`}>
      <div className="flex items-center gap-2 mb-3">
        <Icon className="w-4 h-4" style={{ color: tone === "rose" ? "#e11d48" : NAVY }}/>
        <div className="font-heading text-lg font-semibold" style={{ color: NAVY }}>{title}</div>
      </div>
      <div className="grid grid-cols-3 gap-2">
        {posts.map((p) => (
          <a key={p.id} href={p.post_url} target="_blank" rel="noopener noreferrer"
            data-testid={`social-post-${p.id}`}
            className="relative aspect-square rounded-lg overflow-hidden bg-slate-100 group">
            <ImageWithFallback src={p.thumbnail_url} alt={p.title || ""} className="w-full h-full object-cover group-hover:scale-105 transition-transform"/>
            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors flex items-end p-2">
              <ArrowRight className="w-4 h-4 text-white opacity-0 group-hover:opacity-100"/>
            </div>
          </a>
        ))}
      </div>
    </div>
  );
}

function NewsSkeletonHero() {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
      <div className="lg:col-span-1 rounded-2xl bg-white border border-slate-200 overflow-hidden">
        <div className="aspect-[16/9] bg-slate-100 animate-pulse"/>
        <div className="p-6 space-y-2">
          <div className="h-3 bg-slate-100 rounded animate-pulse w-1/3"/>
          <div className="h-6 bg-slate-100 rounded animate-pulse w-full"/>
          <div className="h-4 bg-slate-100 rounded animate-pulse w-2/3"/>
        </div>
      </div>
      <div className="grid gap-4">
        {[1, 2].map(i => (
          <div key={i} className="rounded-2xl bg-white border border-slate-200 overflow-hidden">
            <div className="aspect-[16/9] bg-slate-100 animate-pulse"/>
            <div className="p-4 space-y-2">
              <div className="h-3 bg-slate-100 rounded animate-pulse w-1/2"/>
              <div className="h-4 bg-slate-100 rounded animate-pulse w-full"/>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function NewsErrorState({ message, onRetry }) {
  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-10 text-center" data-testid="noticias-error">
      <AlertCircle className="w-10 h-10 mx-auto text-rose-300 mb-3"/>
      <div className="font-semibold text-slate-800">Não foi possível carregar as notícias agora.</div>
      <p className="text-sm text-slate-500 mt-1 max-w-md mx-auto">
        {String(message).slice(0, 200)}
      </p>
      <button onClick={onRetry} data-testid="noticias-retry"
        className="mt-4 h-9 px-4 rounded-lg text-white text-sm font-semibold hover:opacity-95"
        style={{ background: NAVY }}>
        <RefreshCw className="w-3.5 h-3.5 inline mr-1"/> Tentar novamente
      </button>
    </div>
  );
}

function NewsEmptyState({ onRetry }) {
  return (
    <div className="bg-white rounded-2xl border border-dashed border-slate-300 p-10 text-center" data-testid="noticias-empty">
      <Newspaper className="w-10 h-10 mx-auto text-slate-300 mb-3"/>
      <div className="font-semibold text-slate-800">Nenhuma notícia disponível no momento.</div>
      <p className="text-sm text-slate-500 mt-1 max-w-md mx-auto">
        A próxima coleta central acontece a cada 6h. Quando novas matérias forem publicadas nas fontes oficiais, elas aparecerão aqui.
      </p>
      <button onClick={onRetry} data-testid="noticias-empty-retry"
        className="mt-4 h-9 px-4 rounded-lg border border-slate-200 bg-white text-slate-700 text-sm font-semibold hover:border-slate-300">
        <RefreshCw className="w-3.5 h-3.5 inline mr-1"/> Atualizar
      </button>
    </div>
  );
}

function ExplainDrawer({ article, onClose }) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    let stop = false;
    api.post(`/news/${article.id}/explain`)
      .then((r) => { if (!stop) setData(r.data); })
      .catch((e) => { if (!stop) setError(e?.response?.data?.detail || "Serviço indisponível"); })
      .finally(() => !stop && setLoading(false));
    return () => { stop = true; };
  }, [article.id]);

  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-stretch justify-end" onClick={onClose}
         data-testid="news-explain-drawer">
      <div className="w-full max-w-2xl bg-white h-full overflow-y-auto shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-slate-200 bg-gradient-to-r from-amber-50 to-white flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg flex items-center justify-center"
               style={{ background: `${GOLD}22`, border: `1px solid ${GOLD}66`, color: GOLD }}>
            <Sparkles className="w-5 h-5"/>
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-[0.24em] font-bold" style={{ color: GOLD }}>
              Por que isso importa?
            </div>
            <div className="font-heading text-lg font-semibold truncate" style={{ color: NAVY }}>
              {article.title}
            </div>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-lg hover:bg-slate-100 inline-flex items-center justify-center text-slate-500">
            <X className="w-4 h-4"/>
          </button>
        </div>

        <div className="p-6">
          {loading && <div className="flex justify-center py-10"><Loader2 className="w-8 h-8 animate-spin text-amber-500"/></div>}
          {error && (
            <div className="rounded-lg border border-rose-200 bg-rose-50 p-4 text-sm text-rose-800">
              <AlertCircle className="w-4 h-4 inline mr-1"/> {error}
            </div>
          )}
          {data && (
            <div className="space-y-4">
              <ExplainBlock title="Resumo" body={data.summary}/>
              <ExplainBlock title="Impacto para a advocacia" body={data.impact}/>
              {data.areas?.length > 0 && (
                <div>
                  <div className="text-[10.5px] uppercase tracking-widest text-slate-500 mb-1.5">Áreas relacionadas</div>
                  <div className="flex flex-wrap gap-1.5">
                    {data.areas.map((a, i) => (
                      <span key={i} className="text-[11.5px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 border border-slate-200">{a}</span>
                    ))}
                  </div>
                </div>
              )}
              {data.consequences && <ExplainBlock title="Possíveis consequências práticas" body={data.consequences}/>}
              <div className="text-[11px] text-slate-500 border-t border-slate-100 pt-3">
                <AlertCircle className="w-3 h-3 inline mr-1"/> Análise gerada pelo Hermes a partir da informação disponível. Consulte a fonte oficial para o conteúdo integral.
              </div>
              <a href={article.original_url} target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center gap-1 h-9 px-4 rounded-lg text-white text-sm font-semibold hover:opacity-95"
                style={{ background: NAVY }}>
                Ver matéria na fonte <ExternalLink className="w-3.5 h-3.5"/>
              </a>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function ExplainBlock({ title, body }) {
  if (!body) return null;
  return (
    <div>
      <div className="text-[10.5px] uppercase tracking-widest text-slate-500 mb-1.5">{title}</div>
      <p className="text-[13px] text-slate-800 leading-relaxed whitespace-pre-wrap">{body}</p>
    </div>
  );
}
